/**
 * IntegrationListing — top-level composition component
 *
 * Renders the complete "Integrations" section of the BPA Dashboard:
 *  1. IntegrationCards — wizard for adding new cloud integrations
 *  2. Integration listing grid with filters, insights, and bulk actions
 *
 * Follows the exact same structural pattern as AccountListing/index.tsx
 * and EPDashboard/EPList/index.tsx.
 */

import LoadingScreen from 'components/LoadingScreen';
import PageLayout from 'components/PageLayout';
import { FilterConfig } from 'components/SearchAndFilters/FilterTypes';
import ActiveFiltersBar from 'components/SearchAndFilters/SelectedFiltersBar';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { Suspense, useLayoutEffect, useRef } from 'react';
import { CardTitle, Col, Row, Collapse } from 'reactstrap';

import IntegrationCards from './components/IntegrationCards';
import IntegrationListingBulkActions from './components/IntegrationListingBulkActions';
import { useIntegrationFilterConfig } from './components/IntegrationListingFilters/Filters.config';
import IntegrationListingFilters from './components/IntegrationListingFilters/IntegrationListingFilters';
import IntegrationListingGrid from './components/IntegrationListingGrid/IntegrationListingGrid';
import IntegrationListingInsights from './components/IntegrationListingInsights/IntegrationListingInsights';
import {
  componentHeightAtom,
  filterValuesAtom,
  integrationDataAtom,
  isIntegrationCardsVisibleAtom,
  useOnFilterChangeResetRelatedAtoms,
} from './state';

// ---------------------------------------------------------------------------
// Sub-components — keep them small and colocated so the file stays readable
// ---------------------------------------------------------------------------

/**
 * Renders the applied-filter chips below the filter bar.
 * Resets filters via the config object rather than clearing atoms directly
 * so the filter UI stays in sync.
 */
const SelectedFilters: React.FC<{ filtersConfig: FilterConfig }> = ({
  filtersConfig,
}) => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const resetOnFilterChange = useOnFilterChangeResetRelatedAtoms();

  return (
    <Suspense fallback={<></>}>
      <ActiveFiltersBar
        filtersConfig={filtersConfig}
        onFiltersReset={() => {
          setFilterValues({ ...filtersConfig.getActiveFilterValues() });
          resetOnFilterChange();
        }}
      />
    </Suspense>
  );
};

/** Displays the record count in the heading; shows "…" while loading */
const ListingCount: React.FC = () => {
  const listingData = useAtomValue(integrationDataAtom);
  return <>{listingData.loading ? '...' : listingData.count}</>;
};

// ---------------------------------------------------------------------------
// Main listing component
// ---------------------------------------------------------------------------

const IntegrationListingContent: React.FC = () => {
  const filtersConfig = useIntegrationFilterConfig();
  const setComponentHeight = useSetAtom(componentHeightAtom);
  const targetRef = useRef<HTMLDivElement>(null);
  const isIntegrationCardsVisible = useAtomValue(isIntegrationCardsVisibleAtom);

  // Track component height so the parent can render a correctly-sized skeleton
  useLayoutEffect(() => {
    if (targetRef.current) {
      setComponentHeight(targetRef.current.offsetHeight);
    }
  }, [setComponentHeight]);

  return (
    <div ref={targetRef}>
      {/* ── Integrations listing ── */}
      <PageLayout
        id="bpa_integrations_listing"
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  Integrations
                  <label id="integration_counts" className="label btn">
                    <Suspense fallback={<>...</>}>
                      <ListingCount />
                    </Suspense>
                  </label>
                </h4>
              </CardTitle>
            </Col>
          </Row>
        }
        subheading="List of cloud integrations for Best Practice Assessment"
        filteringOptions={
          <Suspense fallback={<>...</>}>
            <IntegrationListingFilters filtersConfig={filtersConfig} />
          </Suspense>
        }
      >
        <Suspense fallback={<LoadingScreen />}>
          <div className="container-fluid p-0">
            {/* ── New Integration section (Expandable) ── */}
            <Collapse isOpen={isIntegrationCardsVisible}>
              <div className="mb-4 bpa-new-integration-section">
                <IntegrationCards />
                <hr className="mt-4 mb-2" />
              </div>
            </Collapse>

            {/* Filter chips row */}
            <div className="row mb-1">
              {/* Left: active filter chips */}
              <div className="col">
                <SelectedFilters filtersConfig={filtersConfig} />
              </div>

              {/* Right: bulk actions (refresh / clear selection) */}
              <div className="col-auto text-right align-baseline">
                <Suspense fallback={<>...</>}>
                  <IntegrationListingBulkActions />
                </Suspense>
              </div>
            </div>

            {/* Insights summary bar */}
            <Suspense fallback={<LoadingScreen />}>
              <IntegrationListingInsights />
            </Suspense>

            {/* Data grid */}
            <Suspense fallback={<LoadingScreen />}>
              <IntegrationListingGrid />
            </Suspense>
          </div>
        </Suspense>
      </PageLayout>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Export — wraps the inner component in a height-aware Suspense fallback
// ---------------------------------------------------------------------------

/**
 * Default export consumed by BPADashboard/index.tsx.
 * The outer Suspense uses the last-known component height so the loading
 * skeleton roughly matches the content height.
 */
const IntegrationListing: React.FC = () => {
  const componentHeight = useAtomValue(componentHeightAtom);

  return (
    <Suspense fallback={<LoadingScreen height={componentHeight} />}>
      <IntegrationListingContent />
    </Suspense>
  );
};

export default IntegrationListing;
